import React from 'react'
import ReactMarkdown from 'react-markdown'
import { Image } from '@educator/components/Image'

export type UrgencyBannerMessage = {
  body: string
  emphasis: string
}

// TODO: Replace with animated video once a permanent CDN URL is available
const POWER_AND_INFLUENCE_IMAGE_URL =
  'https://static.masterclass.com/jarvis-enrollment-Power-and-Influence.png'

type Tone = 'yellow' | 'orangeDeep'

const TONE_BG: Record<Tone, string> = {
  yellow: 'bg-ja-yellow-400',
  orangeDeep: 'bg-ja-orange-600',
}

type UrgencyBannerVariantProps =
  | {
      message: UrgencyBannerMessage
      lpBanner?: never
    }
  | {
      // Enrollment-window copy: admin-authored markdown, rendered inline.
      message?: never
      lpBanner: string[]
    }

// An inline action rendered after the message (e.g. "Click here to fix it").
// A button rather than a link because it drives in-app behavior (opening a
// modal) rather than navigation.
type UrgencyBannerCta = {
  label: string
  onClick: () => void
}

type UrgencyBannerProps = UrgencyBannerVariantProps & {
  /**
   * When true (default), reserves space at the top to clear a fixed-position
   * navigation. Set to `false` when the banner sits below a header that is
   * already in normal document flow (e.g. enrollment Overview page) or
   * inside a fixed-position container (e.g. Status Portal bottom-pinned).
   */
  withTopOffset?: boolean
  tone?: Tone
  cta?: UrgencyBannerCta
}

export const UrgencyBanner = ({
  withTopOffset = true,
  tone = 'yellow',
  cta,
  ...props
}: UrgencyBannerProps) => {
  const textClassName =
    'text-base leading-[1.2] tracking-[0.01em] text-black text-left md:text-center'

  return (
    <div
      role='status'
      className={`relative z-[2]${withTopOffset ? ' mt-[66px] md:mt-10' : ''} w-full flex items-center justify-center px-4 py-2 ${TONE_BG[tone]}`}
    >
      <div className='flex items-center gap-1'>
        <Image
          src={POWER_AND_INFLUENCE_IMAGE_URL}
          alt=''
          width={40}
          height={40}
          className='shrink-0 object-cover'
        />
        <div className='p-2'>
          {props.lpBanner ? (
            // Render admin-authored markdown inline so it keeps the banner's
            // typography instead of block styling. Only inline elements are
            // allowed and any block element (list, heading, blockquote, and the
            // wrapping `p`) is unwrapped to its children — otherwise a block tag
            // in the copy would emit invalid DOM inside this `<p>` and break the
            // banner layout. The inline `<span>` per message stays valid because
            // nothing block-level can reach it.
            <p className={textClassName}>
              {props.lpBanner.map((message, index) => (
                // Key on index too: messages are a static, non-reorderable list
                // and may legitimately repeat, which would collide on text alone.
                <React.Fragment key={`${message}-${index}`}>
                  {index > 0 ? ' ' : null}
                  <span>
                    <ReactMarkdown
                      allowedElements={[
                        'a',
                        'strong',
                        'em',
                        'del',
                        'code',
                        'br',
                      ]}
                      unwrapDisallowed
                      components={{
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-semibold underline'
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {message}
                    </ReactMarkdown>
                  </span>
                </React.Fragment>
              ))}
            </p>
          ) : (
            <p className={textClassName}>
              {props.message.body.trim()}{' '}
              <strong>{props.message.emphasis.trim()}</strong>
              {cta && (
                <>
                  {' '}
                  <button
                    type='button'
                    onClick={cta.onClick}
                    className='cursor-pointer font-semibold underline'
                  >
                    {cta.label}
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
